import type { GeneralInboundParams } from './useGetInboundList'

// ── 검색조건 저장/복원 (menuOnSession — ST09·MA02 준용) ──────────────────────────
// 마지막 조회 조건을 메뉴 세션(sessionStorage)·URL 쿼리에 남겨 다른 메뉴를 다녀와도 유지한다(MOMS 공통 UX).
// 탭 전환은 react-router 언마운트라 로컬 state 가 전부 날아간다 — 조건은 컴포넌트 밖(URL)에 둬야 살아남는다.
// 복원 경로는 sessionStorage 가 아니라 URL 이다. sessionStorage 는 탭바·LNB 가 링크를 조립할 때 읽는 운반체다.
//
// 저장 형태는 GeneralInboundParams 그대로(_seq 제외) — Utils.qsStringify(comma) ↔ useParsedQuery(comma) 왕복.
// 다건은 항목이 하나면 파서가 문자열을 주므로 asRestoredList 로 양쪽을 모두 받는다.

/**
 * 저장 대상 판정 — 화면에 되돌릴 수 없는 필드는 저장하지 않는다.
 * 필드는 비었는데 결과만 걸러져 있으면 사용자가 왜 이것만 나오는지 알 수 없고 초기화 말고 풀 방법도 없다.
 *
 * 판정 기준은 컴포넌트의 props 타입이 아니라 실제로 값이 들어가는지다. 조회영역 필드는 전부 제어형이라
 * `GeneralInboundFilter` 가 `initialParams` 로 state 초기값을 잡는다(마운트 시 1회).
 *
 * | 필드 | 컴포넌트 | 복원 통로 | 저장 |
 * | --- | --- | --- | --- |
 * | 화주명 | core.Select(제어형) | initialParams.shipperCode | 함 |
 * | 입고번호 | MultiCodeInput(제어형, 산출물 사본) | initialParams.ordOptionNumbers | 함 |
 * | 목록 검색어 | core.Input.Search(제어형) | listQuery prop | 함 |
 *
 * 단위 테스트는 필드를 스텁으로 대체하므로 우리 배선만 증명한다 — 실제 표시는
 * `GeneralInboundFilter.render.test.tsx` 와 브라우저(LNB 진입; setSearch 는 세션에 등록된 탭에서만 저장된다)에서 본다.
 *
 * 페이지 번호는 저장하지 않는다. 이 화면은 as-is 파리티로 전 행을 한 번에 그려(GR01_PAGE_SIZE 10,000,
 * 컨덕터 pagerSizeChanger='hide') 되돌릴 페이지 상태 자체가 없다. 없는 상태를 저장하면 동작하는 것처럼 보이는 코드만 남는다.
 */

/**
 * 세션·URL 에 싣는 검색조건 모양.
 *
 * 조회 시퀀스(_seq)는 화면 안에서만 쓰는 값이라 싣지 않는다.
 * 목록 검색어(listQuery)는 조회 조건이 아니라 로드된 행을 걸러 보는 값인데, 이것도 함께 싣는다 —
 * 조건은 남았는데 보고 있던 목록 검색어가 풀리면 돌아왔을 때 다른 화면으로 보인다.
 */
export interface GeneralInboundSearchQuery {
  shipperCode?: string
  ordOptionNumbers?: string[]
  listQuery?: string
}

/** 세션·URL 에 저장할 형태로 만든다. 빈 값은 키 자체를 빼 쿼리스트링을 짧게 유지한다. */
export const toSearchQuery = (params: GeneralInboundParams, listQuery = ''): GeneralInboundSearchQuery => ({
  ...(params.shipperCode ? { shipperCode: params.shipperCode } : {}),
  ...(params.ordOptionNumbers?.length ? { ordOptionNumbers: params.ordOptionNumbers } : {}),
  ...(listQuery.trim() ? { listQuery: listQuery.trim() } : {}),
})

/** URL 쿼리 → 목록 검색어. 조작된 URL(배열·객체)은 빈 문자열로 떨어뜨린다 */
export const restoredListQuery = (query: Record<string, unknown>): string =>
  typeof query.listQuery === 'string' ? query.listQuery : ''

/** 문자열 배열만 남긴다 — comma 파서의 단건 문자열/다건 배열 겸용, 조작된 URL(객체 등)은 버린다 */
export const asRestoredList = (value: unknown): string[] | undefined => {
  if (Array.isArray(value) && value.length && typeof value[0] !== 'object') return value.map(String)
  return typeof value === 'string' && value.trim() ? [value.trim()] : undefined
}

/**
 * URL 쿼리(파싱 결과) → 복원 검색조건. 화주코드도 입고번호도 없으면 복원 대상이 아니다(undefined).
 * 그때는 화면이 세션 화주 기본값으로 연다.
 * 조작된 URL 이 폼이나 조회 본문을 깨뜨리지 않도록 타입이 맞는 값만 받는다.
 */
export const fromSearchQuery = (query: Record<string, unknown>): GeneralInboundParams | undefined => {
  const shipperCode = typeof query.shipperCode === 'string' && query.shipperCode ? query.shipperCode : undefined
  const ordOptionNumbers = asRestoredList(query.ordOptionNumbers)
  if (!shipperCode && !ordOptionNumbers) return undefined
  return { shipperCode, ordOptionNumbers }
}
